// IndexedDB für Offline-Datenspeicherung
const DB_NAME = 'meinelisten-offline';
const DB_VERSION = 1;

interface OfflineChange {
  id: string;
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  entity: 'item' | 'list';
  tempItemId?: string;  // Reference to temp UI item (for CREATE operations)
  data: any;
  timestamp: number;
}

class OfflineDB {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Store für Offline-Änderungen (Sync Queue)
        if (!db.objectStoreNames.contains('offline-queue')) {
          const queueStore = db.createObjectStore('offline-queue', { keyPath: 'id' });
          queueStore.createIndex('timestamp', 'timestamp');
        }

        // Store für gecachte Listen
        if (!db.objectStoreNames.contains('lists')) {
          const listsStore = db.createObjectStore('lists', { keyPath: 'id' });
          listsStore.createIndex('updatedAt', 'updatedAt');
        }

        // Store für gecachte Items
        if (!db.objectStoreNames.contains('items')) {
          const itemsStore = db.createObjectStore('items', { keyPath: 'id' });
          itemsStore.createIndex('listId', 'listId');
        }
      };
    });
  }

  // Offline-Änderung zur Queue hinzufügen
  async addToQueue(change: Omit<OfflineChange, 'id' | 'timestamp'>): Promise<void> {
    if (!this.db) await this.init();

    const queueItem: OfflineChange = {
      ...change,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offline-queue'], 'readwrite');
      const store = transaction.objectStore('offline-queue');
      const request = store.add(queueItem);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Alle Offline-Änderungen abrufen
  async getQueue(): Promise<OfflineChange[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offline-queue'], 'readonly');
      const store = transaction.objectStore('offline-queue');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Offline-Änderung aus Queue entfernen
  async removeFromQueue(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offline-queue'], 'readwrite');
      const store = transaction.objectStore('offline-queue');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Liste speichern
  async saveList(list: any): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['lists'], 'readwrite');
      const store = transaction.objectStore('lists');
      const request = store.put(list);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Liste abrufen
  async getList(id: string): Promise<any> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['lists'], 'readonly');
      const store = transaction.objectStore('lists');
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Alle Listen abrufen
  async getAllLists(): Promise<any[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['lists'], 'readonly');
      const store = transaction.objectStore('lists');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Item speichern
  async saveItem(item: any): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['items'], 'readwrite');
      const store = transaction.objectStore('items');
      const request = store.put(item);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Items einer Liste abrufen
  async getListItems(listId: string): Promise<any[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['items'], 'readonly');
      const store = transaction.objectStore('items');
      const index = store.index('listId');
      const request = index.getAll(listId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Item löschen
  async deleteItem(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['items'], 'readwrite');
      const store = transaction.objectStore('items');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const offlineDB = new OfflineDB();
